import React, { useEffect, useState } from "react";
import ActionButton from "../../components/ui/ActionButton";
import AdvancedFilters from "../../components/ui/AdvancedFilters";
import { PageHeading } from "../../components/ui/PageHeading";
import SearchInput from "../../components/ui/SearchInput";
import DeleteModal from "../../components/ui/DeleteModal";
import { useDeleteShiftMutation } from "../../features/shifts/shiftApi";
import { withAuditFilters } from "../masters/filterMetadata";
import useResolvedFilterFields from "../masters/useResolvedFilterFields";
import ShiftForm from "./ShiftForm";
import ShiftTable from "./ShiftTable";
import { usePlantSelection } from "../../features/plants/PlantSelectionContext";

const shiftFilterFields = withAuditFilters([
  { name: "shift_id", label: "Shift ID", optionSource: "shifts" },
  { name: "shift_name", label: "Shift Name" },
  { name: "start_time", label: "Start Time", type: "time" },
  { name: "end_time", label: "End Time", type: "time" },
  {
    name: "shift_status",
    label: "Status",
    type: "select",
    options: [
      { value: "true", label: "Active" },
      { value: "false", label: "Inactive" },
    ],
  },
]);

const Shift = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { selectedPlantId } = usePlantSelection();
  const [filterValues, setFilterValues] = useState({});
  const [editData, setEditData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const resolvedFilterFields = useResolvedFilterFields(
    shiftFilterFields,
    selectedPlantId
  );

  const [deleteShift] = useDeleteShiftMutation();

  useEffect(() => {
    setFilterValues({});
  }, [selectedPlantId]);

  const openCreateForm = () => {
    setEditData(null);
    setIsFormOpen(true);
  };

  const openEditForm = (row) => {
    setEditData(row);
    setIsFormOpen(true);
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    setIsDeleteOpen(true);
  };

  const handleDeleteAction = async (id) => {
    if (!id) return;

    try {
      await deleteShift(id).unwrap();
    } catch (error) {
      console.error("Delete failed", error);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilterValues((current) => ({
      ...current,
      [name]: value,
    }));
  };

  return (
    <div className="px-3 sm:px-5">
      <PageHeading title="Shifts" />

      <div className="flex flex-col gap-3 pb-3 lg:flex-row lg:items-center lg:justify-between">
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search shift"
        />
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
          <AdvancedFilters
            fields={resolvedFilterFields}
            values={filterValues}
            onChange={handleFilterChange}
            onClear={() => setFilterValues({})}
            requirePlant
            hasPlant={Boolean(selectedPlantId)}
          />
          <ActionButton
            action="add"
            label="Add Shift"
            onClick={openCreateForm}
            className="w-full sm:w-auto"
            disabled={!selectedPlantId}
            title={!selectedPlantId ? "Select a plant from the top bar first" : undefined}
          />
        </div>
      </div>

      <ShiftTable
        searchTerm={searchTerm}
        selectedPlantId={selectedPlantId}
        filters={filterValues}
        onEditClick={openEditForm}
        onDeleteClick={openDeleteModal}
      />

      <ShiftForm
        editData={editData}
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        selectedPlantId={selectedPlantId}
      />

      <DeleteModal
        isOpen={isDeleteOpen}
        closeModal={() => setIsDeleteOpen(false)}
        handleDeleteAction={handleDeleteAction}
        itemId={deleteId}
      />
    </div>
  );
};

export default Shift;
